import constants from '@playcastdotio/GlobalConfig';
import * as Sentry from '@sentry/react';
import { captureBrowserData, BrowserData } from './browserDataCapture';
import {
  PlaycastAnalyticsEvent,
  PlaycastAnalyticsTier1Filter,
  PlaycastAnalyticsTier2Filter,
  PlaycastAnalyticsVersion,
} from './types';
import type { AnalyticsBase } from '@playcastdotio/AnalyticsCore';

type AnalyticsEvent = PlaycastAnalyticsEvent | AnalyticsBase;

export interface AwsTransportContext {
  version: PlaycastAnalyticsVersion;
  debugMode: boolean;
  browserData?: BrowserData;
}

type AwsAnalyticsPayload = ReturnType<typeof buildAwsPayload>;

interface PendingAwsEvent {
  payload: AwsAnalyticsPayload;
  attempt: number;
  nextEligibleAt: number;
}

interface PendingRealtimeEvent {
  payload: AwsAnalyticsPayload;
  attempt: number;
  nextEligibleAt: number;
}

const MAX_QUEUE_LENGTH = 20;
// Total attempts = 1 primary send + 3 retries = 4
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [1500, 4000, 8000];
// Realtime-only jitter helper; AWS path uses fixed delays.
const withJitter = (baseMs: number): number =>
  baseMs + Math.floor((Math.random() - 0.5) * 0.5 * baseMs);
const KEEPALIVE_PROBE_URL = 'https://www.google-analytics.com/g/collect';
const KEEPALIVE_PROBE_TIMEOUT_MS = 1500;
const AWS_FETCH_TIMEOUT_MS = 2500;

const awsRetryQueue: PendingAwsEvent[] = [];
let awsQueueTimer: ReturnType<typeof setTimeout> | null = null;
let awsQueueProcessing = false;
let cachedKeepaliveSupport: boolean | null = null;
let beaconHandlersRegistered = false;
let keepaliveProbePromise: Promise<boolean> | null = null;

const realtimeRetryQueue: PendingRealtimeEvent[] = [];
let realtimeQueueTimer: ReturnType<typeof setTimeout> | null = null;
let realtimeQueueProcessing = false;

const getAwsAnalyticsUrl = (): string => constants.ANALYTICS_API_URL;
const getRealtimeApiUrl = (): string => `${constants.REALTIME_API_BASE_URL}realtime/events/track`;
// NOTE: During dual-write validation, GA4 will receive events from both the Lambda
// (primary) and realtime-api (secondary). This intentional double-count is temporary
// and will be resolved when the Lambda is decommissioned.
const DUAL_WRITE_ENABLED = true;
const shouldDualWrite = (): boolean => DUAL_WRITE_ENABLED;

const captureQueueWarning = (message: string, extra: Record<string, unknown>) => {
  if (typeof Sentry.isInitialized === 'function' && Sentry.isInitialized()) {
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: {
        analytics_method: 'aws_direct',
        analytics_component: 'aws_transport_queue',
      },
      extra,
    });
  }
};

const captureRealtimeQueueWarning = (message: string, extra: Record<string, unknown>) => {
  if (typeof Sentry.isInitialized === 'function' && Sentry.isInitialized()) {
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: {
        analytics_method: 'aws_direct',
        analytics_component: 'realtime_transport_queue',
      },
      extra,
    });
  }
};

async function runKeepaliveProbe(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') {
    return false;
  }

  try {
    new Request(KEEPALIVE_PROBE_URL, {
      method: 'POST',
      body: 'probe',
      keepalive: true,
    });
  } catch {
    return false;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), KEEPALIVE_PROBE_TIMEOUT_MS);

  try {
    await fetch(KEEPALIVE_PROBE_URL, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      keepalive: true,
      signal: controller.signal,
    });
    return true;
  } catch (error) {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function supportsKeepaliveFetch(): Promise<boolean> {
  if (cachedKeepaliveSupport !== null) {
    return cachedKeepaliveSupport;
  }

  if (!keepaliveProbePromise) {
    keepaliveProbePromise = runKeepaliveProbe().then((result) => {
      cachedKeepaliveSupport = result;
      return result;
    }).catch((error) => {
      cachedKeepaliveSupport = false;
      return false;
    });
  }

  return keepaliveProbePromise;
}

const scheduleAwsQueueProcessing = (delayMs: number) => {
  if (awsQueueTimer !== null) {
    return;
  }

  awsQueueTimer = setTimeout(() => {
    awsQueueTimer = null;
    void processAwsRetryQueue();
  }, delayMs);
};

const now = () => Date.now();

const enqueueAwsRetry = (payload: AwsAnalyticsPayload, attempt = 0, nextEligibleAt = now()) => {
  if (awsRetryQueue.length >= MAX_QUEUE_LENGTH) {
    const dropped = awsRetryQueue.shift();
    captureQueueWarning('analytics_aws_retry_queue_dropped_oldest', {
      droppedEvent: dropped?.payload.eventName ?? 'unknown',
      droppedAttempt: dropped?.attempt ?? -1,
      queueLength: awsRetryQueue.length,
    });
  }

  awsRetryQueue.push({ payload, attempt, nextEligibleAt });
  registerBeaconFlushHandlers();
  const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
  scheduleAwsQueueProcessing(delay);
};

const scheduleRealtimeQueueProcessing = (delayMs: number) => {
  if (realtimeQueueTimer !== null) {
    return;
  }

  realtimeQueueTimer = setTimeout(() => {
    realtimeQueueTimer = null;
    void processRealtimeRetryQueue();
  }, delayMs);
};

const enqueueRealtimeRetry = (payload: AwsAnalyticsPayload, attempt = 0, nextEligibleAt = now()) => {
  if (realtimeRetryQueue.length >= MAX_QUEUE_LENGTH) {
    const dropped = realtimeRetryQueue.shift();
    captureRealtimeQueueWarning('analytics_realtime_retry_queue_dropped_oldest', {
      droppedEvent: dropped?.payload.eventName ?? 'unknown',
      droppedAttempt: dropped?.attempt ?? -1,
      queueLength: realtimeRetryQueue.length,
    });
  }

  realtimeRetryQueue.push({ payload, attempt, nextEligibleAt });
  registerBeaconFlushHandlers();
  const delay = withJitter(RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]);
  scheduleRealtimeQueueProcessing(delay);
};

const flushAwsQueueWithBeacon = () => {
  if (
    awsRetryQueue.length === 0 ||
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    return;
  }

  let strandedCount = 0;
  while (awsRetryQueue.length > 0) {
    const pending = awsRetryQueue.shift();
    if (!pending) {
      break;
    }

    try {
      const body = new Blob([JSON.stringify(pending.payload)], {
        type: 'application/json',
      });
      const success = navigator.sendBeacon(getAwsAnalyticsUrl(), body);
      if (!success) {
        awsRetryQueue.unshift(pending);
        strandedCount = awsRetryQueue.length;
        break;
      }
    } catch (error) {
      awsRetryQueue.unshift(pending);
      strandedCount = awsRetryQueue.length;
      break;
    }
  }

  if (strandedCount > 0) {
    captureQueueWarning('analytics_beacon_flush_stranded', {
      strandedCount,
      queue: 'aws',
    });
  }
};

const flushRealtimeQueueWithBeacon = () => {
  if (
    realtimeRetryQueue.length === 0 ||
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    return;
  }

  let strandedCount = 0;
  while (realtimeRetryQueue.length > 0) {
    const pending = realtimeRetryQueue.shift();
    if (!pending) {
      break;
    }

    try {
      const body = new Blob([JSON.stringify(pending.payload)], {
        type: 'application/json',
      });
      const success = navigator.sendBeacon(getRealtimeApiUrl(), body);
      if (!success) {
        realtimeRetryQueue.unshift(pending);
        strandedCount = realtimeRetryQueue.length;
        break;
      }
    } catch (error) {
      realtimeRetryQueue.unshift(pending);
      strandedCount = realtimeRetryQueue.length;
      break;
    }
  }

  if (strandedCount > 0) {
    captureRealtimeQueueWarning('analytics_beacon_flush_stranded', {
      strandedCount,
      queue: 'realtime',
    });
  }
};

const handleVisibilityChange = () => {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    flushAwsQueueWithBeacon();
    flushRealtimeQueueWithBeacon();
  }
};

const registerBeaconFlushHandlers = () => {
  if (beaconHandlersRegistered || typeof window === 'undefined') {
    return;
  }

  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return;
  }

  const flush = () => {
    flushAwsQueueWithBeacon();
    flushRealtimeQueueWithBeacon();
  };

  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);
  window.addEventListener('visibilitychange', handleVisibilityChange);

  beaconHandlersRegistered = true;
};

const sendPayload = async (payload: AwsAnalyticsPayload, url: string, allowKeepalive: boolean): Promise<Response> => {
  const requestInit: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  };

  if (allowKeepalive && (await supportsKeepaliveFetch())) {
    requestInit.keepalive = true;
  }

  const controller = new AbortController();
  const timeoutId = typeof window !== 'undefined'
    ? window.setTimeout(() => controller.abort(), AWS_FETCH_TIMEOUT_MS)
    : setTimeout(() => controller.abort(), AWS_FETCH_TIMEOUT_MS);
  requestInit.signal = controller.signal;

  try {
    return await fetch(url, requestInit);
  } finally {
    clearTimeout(timeoutId);
  }
};

const processAwsRetryQueue = async (): Promise<void> => {
  if (awsQueueProcessing) {
    return;
  }

  awsQueueProcessing = true;

  try {
    while (awsRetryQueue.length > 0) {
      const head = awsRetryQueue[0];
      let nextEvent: PendingAwsEvent;

      if (head.nextEligibleAt > now()) {
        const eligibleIdx = awsRetryQueue.findIndex((e) => e.nextEligibleAt <= now());
        if (eligibleIdx === -1) {
          if (awsQueueTimer === null) {
            const delay = Math.max(50, head.nextEligibleAt - now());
            scheduleAwsQueueProcessing(delay);
          }
          break;
        }
        nextEvent = awsRetryQueue.splice(eligibleIdx, 1)[0];
      } else {
        const shifted = awsRetryQueue.shift();
        if (!shifted) {
          break;
        }
        nextEvent = shifted;
      }

      try {
        const response = await sendPayload(nextEvent.payload, getAwsAnalyticsUrl(), true);
        if (!response.ok) {
          throw new Error(`AWS analytics retry failed with status ${response.status}`);
        }
      } catch (error) {
        nextEvent.attempt += 1;
        if (nextEvent.attempt >= MAX_RETRY_ATTEMPTS) {
          captureQueueWarning('analytics_aws_retry_dropped_after_max_attempts', {
            eventName: nextEvent.payload?.eventName ?? 'unknown',
            attempts: nextEvent.attempt,
            error: error instanceof Error ? error.message : String(error),
          });
          continue;
        }

        const backoff = RETRY_DELAYS_MS[Math.min(nextEvent.attempt, RETRY_DELAYS_MS.length - 1)];
        nextEvent.nextEligibleAt = now() + backoff;
        awsRetryQueue.push(nextEvent);
        if (awsQueueTimer === null) {
          scheduleAwsQueueProcessing(backoff);
        }
      }
    }
  } finally {
    awsQueueProcessing = false;
  }
};

const processRealtimeRetryQueue = async (): Promise<void> => {
  if (realtimeQueueProcessing) {
    return;
  }

  realtimeQueueProcessing = true;

  try {
    while (realtimeRetryQueue.length > 0) {
      const head = realtimeRetryQueue[0];
      let nextEvent: PendingRealtimeEvent;

      if (head.nextEligibleAt > now()) {
        const eligibleIdx = realtimeRetryQueue.findIndex((e) => e.nextEligibleAt <= now());
        if (eligibleIdx === -1) {
          if (realtimeQueueTimer === null) {
            const delay = Math.max(50, head.nextEligibleAt - now());
            scheduleRealtimeQueueProcessing(delay);
          }
          break;
        }
        nextEvent = realtimeRetryQueue.splice(eligibleIdx, 1)[0];
      } else {
        const shifted = realtimeRetryQueue.shift();
        if (!shifted) {
          break;
        }
        nextEvent = shifted;
      }

      try {
        const response = await sendPayload(nextEvent.payload, getRealtimeApiUrl(), true);
        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) {
            captureRealtimeQueueWarning('analytics_realtime_4xx_dropped', {
              eventName: nextEvent.payload?.eventName ?? 'unknown',
              status: response.status,
              attempt: nextEvent.attempt,
              phase: 'retry_loop',
            });
            continue;
          }
          throw new Error(`Realtime API retry failed with status ${response.status}`);
        }
      } catch (error) {
        nextEvent.attempt += 1;
        if (nextEvent.attempt >= MAX_RETRY_ATTEMPTS) {
          captureRealtimeQueueWarning('analytics_realtime_retry_dropped_after_max_attempts', {
            eventName: nextEvent.payload?.eventName ?? 'unknown',
            attempts: nextEvent.attempt,
            error: error instanceof Error ? error.message : String(error),
          });
          continue;
        }

        const backoff = withJitter(RETRY_DELAYS_MS[Math.min(nextEvent.attempt, RETRY_DELAYS_MS.length - 1)]);
        nextEvent.nextEligibleAt = now() + backoff;
        realtimeRetryQueue.push(nextEvent);
        if (realtimeQueueTimer === null) {
          scheduleRealtimeQueueProcessing(backoff);
        }
      }
    }
  } finally {
    realtimeQueueProcessing = false;
  }
};

// Generates a v4-ish UUID. Prefers crypto.randomUUID where available, falls
// back to a getRandomValues-based v4, and finally to Math.random for the
// rare environments without crypto. Used as the canonical playcastEventId.
const generatePlaycastEventId = (): string => {
  const cryptoObj = (typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined);
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const buildAwsPayload = (
  event: AnalyticsEvent,
  tier1Filters: PlaycastAnalyticsTier1Filter[],
  tier2Filters: PlaycastAnalyticsTier2Filter[],
  context: AwsTransportContext,
) => {
  const browserData = context.browserData ?? captureBrowserData();
  // Mint the canonical id ONCE per source event. The same payload object is
  // reused on retries, so every retry attempt — and every dual-write target
  // (AWS Lambda + realtime-api) — carries the same playcastEventId. That is
  // what makes cross-pipeline joins and AWS-side dedup possible.
  const playcastEventId = generatePlaycastEventId();

  return {
    eventName: event.name,
    playcastEventId,
    eventParams: {
      ...event,
      ...context.version,
      playcastEventId,
      debug_mode: context.debugMode,
      delivery_method: 'aws_direct',
      tier1Filters: tier1Filters.join(','),
      tier2Filters: tier2Filters.join(','),
      page_location: browserData._browser_context.page_location,
      page_title: browserData._browser_context.page_title,
      ...(browserData._browser_context.referrer && { page_referrer: browserData._browser_context.referrer }),
      ...(browserData._browser_context.utm_string && { utm_string: browserData._browser_context.utm_string }),
      screen_resolution: browserData._browser_context.screen_resolution,
      viewport_width: browserData._browser_context.viewport_width,
      viewport_height: browserData._browser_context.viewport_height,
      connection_type: browserData._browser_context.connection_type,
      ismobile: browserData._browser_context.ismobile,
    },
    ga4Fields: {
      ...(browserData.user_pseudo_id && { user_pseudo_id: browserData.user_pseudo_id }),
      device: browserData.device,
      privacy_info: browserData.privacy_info,
      traffic_source: browserData.traffic_source,
      ...(Object.keys(browserData.collected_traffic_source).length > 0 && {
        collected_traffic_source: browserData.collected_traffic_source,
      }),
      platform: browserData.platform,
      event_dimensions: browserData.event_dimensions,
    },
    ...(browserData.user_pseudo_id && { clientId: browserData.user_pseudo_id }),
    timestamp: Date.now(),
  };
};

export const sendToAwsAnalytics = async (
  event: AnalyticsEvent,
  tier1Filters: PlaycastAnalyticsTier1Filter[],
  tier2Filters: PlaycastAnalyticsTier2Filter[],
  context: AwsTransportContext,
): Promise<Response | void> => {
  const awsPayload = buildAwsPayload(event, tier1Filters, tier2Filters, context);

  if (shouldDualWrite()) {
    sendPayload(awsPayload, getRealtimeApiUrl(), true).then((response) => {
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          captureRealtimeQueueWarning('analytics_realtime_4xx_dropped', {
            eventName: awsPayload?.eventName ?? 'unknown',
            status: response.status,
            phase: 'initial_dual_write',
          });
          return;
        }
        enqueueRealtimeRetry(awsPayload);
      }
    }).catch(() => {
      enqueueRealtimeRetry(awsPayload);
    });
  }

  try {
    const response = await sendPayload(awsPayload, getAwsAnalyticsUrl(), true);

    if (!response.ok) {
      throw new Error(`AWS analytics responded with status ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('[Analytics-AWS] Error sending to AWS endpoint:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      eventName: event.name,
    });

    enqueueAwsRetry(awsPayload);

    return Promise.reject(error);
  }
};


